import React from 'react';
import { Box, Card, CardActionArea, CardContent, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import EditNoteIcon from '@mui/icons-material/EditNote';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { faqSchema, useJsonLd } from '../../hooks/useJsonLd';

const TOOLS = [
  {
    to: '/text-editor',
    icon: <EditNoteIcon color="primary" />,
    title: 'Online Text Editor',
    body: 'Fix PDF line breaks, find & replace with regex, convert Markdown, HTML, CSV, JSON, XML and YAML, and export a formatted PDF.',
  },
  {
    to: '/typing-test',
    icon: <KeyboardIcon color="primary" />,
    title: 'Typing Test & Practice',
    body: 'Check your WPM and accuracy, practise with real paragraphs and guided exercises, with an on-screen finger guide and keyboard sounds.',
  },
  {
    to: '/guides',
    icon: <MenuBookIcon color="primary" />,
    title: 'Guides, Tips & Tricks',
    body: 'Step-by-step how-tos for converting documents, PDF page tools, the text editor and improving your typing speed.',
  },
];

/** Visible on the page and mirrored in FAQPage structured data. */
export const HOME_FAQ: { q: string; a: string }[] = [
  {
    q: 'Is this PDF to text converter really free?',
    a: 'Yes, completely free. There are no sign-ups, no hidden costs and no file limits, and all conversion happens in your browser.',
  },
  {
    q: 'Are my files uploaded to a server?',
    a: 'No. All processing runs entirely in your browser, so your files never leave your device and are never sent to any server.',
  },
  {
    q: 'Can I convert Word (.docx) files to Markdown?',
    a: 'Yes. Upload a Word file and the converter extracts headings, lists, bold and italic text, and tables into clean Markdown.',
  },
  {
    q: 'Can I convert Excel (.xlsx) files to Markdown, CSV or JSON?',
    a: 'Yes. Each sheet becomes a Markdown table or plain text, and you can also download the data as CSV or JSON.',
  },
  {
    q: 'Can I edit the converted text or turn it back into a PDF?',
    a: 'Yes. Click "Open in Text Editor" above the output to clean it up, convert it to other formats, and export it as a formatted PDF with headings, links and bookmarks.',
  },
  {
    q: 'Does it work with scanned PDFs?',
    a: 'Scanned PDFs are images of text with no text layer, so there is nothing to extract directly. Our guide on scanned PDFs explains your options.',
  },
];

const HomeExtras: React.FC = () => {
  useJsonLd('home-faq', faqSchema(HOME_FAQ));

  return (
    <Box sx={{ mt: 6 }}>
      <Box component="section" aria-labelledby="more-tools">
        <Typography id="more-tools" variant="h5" component="h2" gutterBottom>
          More free tools
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
          {TOOLS.map((tool) => (
            <Card key={tool.to} variant="outlined">
              <CardActionArea component={RouterLink} to={tool.to} sx={{ height: '100%' }}>
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    {tool.icon}
                    <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600 }}>{tool.title}</Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">{tool.body}</Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>
      </Box>

      <Box component="section" aria-labelledby="home-faq" sx={{ mt: 5 }}>
        <Typography id="home-faq" variant="h5" component="h2" gutterBottom>
          Frequently asked questions
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, columnGap: 4, rowGap: 2 }}>
          {HOME_FAQ.map((item) => (
            <Box key={item.q}>
              <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600 }}>{item.q}</Typography>
              <Typography variant="body2" color="text.secondary">{item.a}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default HomeExtras;
